import {
  type AvailableCloser,
  type CalendarAvailabilityInput,
  type CalendarAvailabilitySuccess,
  type CalendarEventData,
  MINUTE,
  SECOND,
} from '@terros/common'
import { useProfile } from '../user'
import { useTantalimQuery } from '../query'
import { usePreference } from '../preferences'
import { usePermissionCheck } from '../permissions'
import { calendarAvailabilityKey } from '../keys'
import { type MaterialSymbolType } from '../icons'
import { type GetHookResponse } from '../hooks'

export function useCalendarAvailability(input: CalendarAvailabilityInput, enabled = true): GetHookResponse<boolean> {
  return useTantalimQuery<CalendarAvailabilityInput, CalendarAvailabilitySuccess, boolean>({
    url: '/calendar/availability',
    queryKey: calendarAvailabilityKey(input),
    input,
    convertResponse: (data) => data.available,
    options: {
      gcTime: 5 * MINUTE,
      staleTime: 30 * SECOND,
      enabled,
    },
  })
}

export function getAvailableCloserWarningProps(
  warning: AvailableCloser
): { message: string; icon: MaterialSymbolType } | undefined {
  if (warning.locked)
    return {
      message: 'This closer is locked by open tasks (tasks need to be completed before continuing)',
      icon: 'lock',
    }
  if (!warning.available)
    return {
      message: warning.unavailableReason ?? 'This closer is unavailable',
      icon: 'calendar_lock',
    }
  if (!warning.inServiceArea) return { message: 'This closer is not in service area', icon: 'map' }
  return undefined
}

export function useCanBypassUnavailability(
  event?: Pick<CalendarEventData, 'attendeeId'>
): {
  allowed: boolean
  isLoading: boolean
} {
  const { company } = useProfile()
  const { data: restrictSetterCloseEvents, isLoading: isLoadingPref } = usePreference('restrictSetterCloseEvents')
  const { data: hasManagePerms, isLoading: isLoadingManagePerms } = usePermissionCheck({
    permissionType: 'availability:manage',
    targetId: [company?.companyId, event?.attendeeId],
  })

  return {
    allowed: canBypassUnavailability(Boolean(restrictSetterCloseEvents), Boolean(hasManagePerms)),
    isLoading: isLoadingPref || isLoadingManagePerms,
  }
}

const canBypassUnavailability = (restrictSetterCloseEvents: boolean, hasManagePerms: boolean): boolean => {
  if (!restrictSetterCloseEvents) return true
  if (hasManagePerms) return true
  return false
}
